import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory } from 'react-router';
import { IContactSelection, ICreateCampaignRequest, ILocationState } from '../../../../../models';
import { getDisplayName } from '../../../../../models/NameUtils';
import { debounce } from '../../../../../models/UiUtils';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { toast } from 'sonner';
import { useModal } from '../../../../../models/hooks/useModal';
import {
	invalidateAllContactFilterQueries,
	useContactDeleteManyMutation,
	useContactDeleteMutation,
	useContactSetKeepInTouchMutation,
	useContactSetVisibilityMutation,
	useContactUnarchiveMutation,
	useExportContactsMutation,
	useExportContactsPolicyReportMutation,
} from '../../../../../queries';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../components/ConfirmationDialog';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { ExportConfirmationModalV2 } from '../../../../components/ExportConfirmation';
import { HandwrittenCardTemplateSearchModal } from '../../../../components/HandwrittenCards/HandwrittenCardTemplateSearchModal';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/Popover';
import { AutomationSelectorModal } from '../../../../components/automation/AutomationSelector';
import { AddContactConnection } from '../../../../components/contacts/AddContactConnection';
import { AssignContactOwner } from '../../../../components/contacts/AssignContactOwner';
import { ContactsAddTagModal } from '../../../../components/contacts/ContactsAddTagModal';
import { MergeContactsWizard } from '../../../../components/contacts/MergeContactsWizard';
import { ArchiveIcon } from '../../../../components/svgs/icons/ArchiveIcon';
import { MoreIcon } from '../../../../components/svgs/icons/MoreIcon';
import { WarningIcon } from '../../../../components/svgs/icons/WarningIcon';
import { ROUTES } from '../../../../constants/routes';
import { usePeopleSearch } from '../PeopleContext';
import { useStatusFilters } from '../hooks/useStatusFilter';
import { ActionBarButton } from './ActionBarButton';
import { ButtonRenderer } from './ButtonRenderer';
import { styleSheet } from './styles';
import { useActionBarStatus, useVisibilityJob } from './useActionBarStatus';
import { useActionButtons } from './useActionButtons';
import { useButtonConfiguration } from './useButtonConfiguration';

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	filter: Api.IBulkContactsRequest;
	sort: Api.ISortDescriptor<string>;
	onReload: ({ newFilter, newSort }: { newFilter?: Api.IBulkContactsRequest; newSort?: Api.ISortDescriptor }) => void;
	onChangeFilter: (filter: Api.IBulkContactsRequest) => void;
}

const RestoreConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isCta: true,
		title: 'Restore',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const ConfirmVisibilityChange: IConfirmationDialogOption[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Confirm',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

export const PeopleActionsBar: React.FC<IProps> = observer(
	({ className, styles, filter, sort, onReload, onChangeFilter }) => {
		const userSession = useUserSession();
		const fullscreenModal = useFullscreenModal();
		const history = useHistory();
		const { pushApiError } = useErrorMessages();

		// Use the new context-based approach
		const { selectionState, selectedContactIds, setSelectedContactIds, excludedContactIds, contacts } =
			usePeopleSearch();

		const clearSelectionState = () => {
			setSelectedContactIds([]);
			onReload({});
		};

		// Get display name for a contact
		const getContactDisplayName = (contact: Api.IContact) => {
			return getDisplayName(contact);
		};

		const { isActive, isSelectingAll, canKeepInTouch, canMerge, canRestore, canFocus } = useActionBarStatus();

		const [isContactOwnerModalOpen, setIsContactOwnerModalOpen] = React.useState(false);
		const [isContactConnectionModalOpen, setIsContactConnectionModalOpen] = React.useState(false);
		const [isMergeModalOpen, setIsMergeModalOpen] = React.useState(false);

		const [isAutomationOpen, setIsAutomationOpen] = React.useState(false);
		const [isMoreMenuOpen, setIsMoreMenuOpen] = React.useState(false);
		const { isArchived } = useStatusFilters({ filter, onChangeFilter });

		const exportContactsMutation = useExportContactsMutation({
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});
		const exportContactsPolicyReportMutation = useExportContactsPolicyReportMutation({
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		// Set up contact mutations
		const contactSetKeepInTouchMutation = useContactSetKeepInTouchMutation({
			onSuccess: () => {
				setSelectedContactIds([]);
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		const contactDeleteMutation = useContactDeleteMutation({
			onSuccess: (_result: Api.IBulkOperationResult<string>) => {
				toast.success(`${selectedContactIds.length} contacts deleted`);
				setSelectedContactIds([]);
				onReload({});
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		const contactDeleteManyMutation = useContactDeleteManyMutation({
			onSuccess: (result: number) => {
				toast.success(`${result} contacts deleted`);
				setSelectedContactIds([]);
				onReload({});
			},
			onError: (error: Api.IOperationResultNoValue) => {
				pushApiError(error);
			},
		});

		// Set up visibility and unarchive mutations
		const contactSetVisibilityMutation = useContactSetVisibilityMutation({
			onSuccess: data => {
				startWatchingJob(data);
			},
			onError: error => {
				pushApiError(error);
			},
		});

		const contactUnarchiveMutation = useContactUnarchiveMutation({
			onSuccess: restoredCount => {
				toast.success(
					restoredCount > 1
						? `${restoredCount} contacts were restored`
						: selectedContactIds.length && selectedContacts.length > 0
							? getContactDisplayName(selectedContacts[0]) + ' was restored'
							: `${restoredCount} contact restored`
				);

				setSelectedContactIds([]);
				onReload({});
			},
			onError: error => {
				pushApiError(error);
			},
		});

		const onAutomationsSuccess = () => {
			clearSelectionState();
			setIsAutomationOpen(false);
		};

		// Set up visibility job monitoring
		const { startWatchingJob, isJobRunning: _isJobRunning } = useVisibilityJob(() => {
			// Job completed callback
			onReload({});
			invalidateAllContactFilterQueries();
		});

		const canShowAutomations = React.useRef(userSession?.account?.features?.automation?.enabled).current;
		const canShowCards = React.useRef(userSession?.account?.features?.handwrittenCards?.enabled).current;
		const [visibilityChangeType, setVisibilityChangeType] = React.useState<'admin' | 'all'>(null);
		const [isHwcTemplateChooserModalOpen, setIsHwcTemplateChooserModalOpen] = React.useState(false);
		const [routeAfterHwcTemplateSelection, setRouteAfterHwcTemplateSelection] = React.useState<'contacts' | 'filter'>(
			null
		);

		const canSendTextingCampaign =
			userSession?.account?.features?.texting?.enabled &&
			userSession.account.features.automation?.additionalAllowedTriggers?.includes(Api.AutomationTriggerType.Texting);
		const duplicateContactsFeatureFlag = !!userSession.account.preferences.showDuplicateContacts;

		// Get selected contacts for UI display
		const selectedContacts = React.useMemo(() => {
			return contacts.filter(contact => selectedContactIds.includes(contact.id));
		}, [contacts, selectedContactIds]);

		// Keep in touch functionality
		const onKeepInTouch = async (frequency: number) => {
			if (frequency > -1) {
				const keepInTouches = selectedContacts.map(x => ({
					contactId: x.id,
					accountId: userSession.account.id,
					userId: userSession.user.id,
					frequency,
				}));

				await contactSetKeepInTouchMutation.mutateAsync(keepInTouches);
				invalidateAllContactFilterQueries();
				onReload({});
			}
		};

		const onSendMessage = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				if (selectedContactIds.length === 0) {
					const allWasSelected = filter.includeContactIds?.length === 0;
					if (!allWasSelected) {
						const flatContacts = contacts;

						const contactsToSend: Api.ContactViewModel[] = [];
						flatContacts
							.filter(c => !excludedContactIds.includes(c.id))
							.forEach(x => {
								contactsToSend.push(new Api.ContactViewModel(userSession, x));
								if (x.duplicateContactIds?.length) {
									x.duplicateContactIds.forEach(id => {
										const duplicateContact = contacts.find(c => c.id === id);
										if (duplicateContact) {
											contactsToSend.push(new Api.ContactViewModel(userSession, duplicateContact));
										}
									});
								}
							});

						fullscreenModal.history?.push({
							pathname: '/email/campaigns/create/from-selection',
							state: {
								model: {
									context: {
										allowSenderSelection: true,
										bulkFilterRequestOptions: {
											readOnlySearchCriteria: true,
										},
										contacts: contactsToSend,
									},
									type: 'Contacts',
								},
							},
						});
						return;
					}

					// select all, but check exclusions
					const bulkFilterRequest: Api.IEmailMessageContactsFilterRequest = {
						contactFilterRequest: { criteria: filter.filter.criteria },
						groupByDuplicate: duplicateContactsFeatureFlag,
						ownershipFilter: filter.ownershipFilter,
					};

					// apply sort
					if (sort && sort.sortBy) {
						bulkFilterRequest.sortAscending = sort.sort === 'asc';
						bulkFilterRequest.sortProperty = sort.sortBy as keyof Api.IContact;
					}

					const locationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
						model: {
							context: {
								allowSenderSelection: true,
								bulkFilterRequest,
								contactsToOmit: excludedContactIds?.length
									? excludedContactIds
											.map(id => {
												const contact = contacts.find(x => x.id === id);
												return contact ? new Api.ContactViewModel(userSession, contact) : null;
											})
											.filter(Boolean) // Filter out null/undefined values
									: [],
								contacts: filter.includeContactIds?.length
									? (contacts.filter(x => filter.includeContactIds.includes(x.id)) as any)
									: undefined,
							},
							type: 'Contacts',
						},
					};
					fullscreenModal.history?.push({
						pathname: '/email/campaigns/create/from-filter',
						state: locationState,
					});
				} else {
					// normal selection
					const locationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
						model: {
							context: {
								allowSenderSelection: true,
								bulkFilterRequestOptions: {
									readOnlySearchCriteria: true,
								},
								contacts: selectedContacts.map(x => new Api.ContactViewModel(userSession, x)),
							},
							type: 'Contacts',
						},
					};
					fullscreenModal.history?.push({
						pathname: '/email/campaigns/create/from-selection',
						state: locationState,
					});
				}
			}
		};

		const onSendCard = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				if (selectedContactIds.length === 0) {
					setRouteAfterHwcTemplateSelection('filter');
					setIsHwcTemplateChooserModalOpen(true);
				} else {
					setRouteAfterHwcTemplateSelection('contacts');
					setIsHwcTemplateChooserModalOpen(true);
				}
			}
		};

		const onFocusContacts = () => {
			if (selectionState === Api.ESelectionState.All || selectionState === Api.ESelectionState.Some) {
				const searchParams = new URLSearchParams();
				searchParams.set('mode', 'contact');
				if (selectedContactIds?.length) {
					searchParams.set('initialContactIds', selectedContactIds.join(','));
				}
				if (excludedContactIds?.length) {
					searchParams.set('initialContactIdsToOmit', excludedContactIds.join(','));
				}
				if (filter?.filter?.criteria) {
					searchParams.set('initialFilterCriteriaList', JSON.stringify(filter.filter.criteria));
				}
				if (filter?.ownershipFilter) {
					searchParams.set('ownershipFilter', JSON.stringify(filter.ownershipFilter));
				}
				history.push({
					pathname: `${ROUTES.contacts.people}/focused`,
					search: searchParams.toString(),
				});
			}
		};

		const onSendTextClicked = () => {
			if (selectedContactIds.length > 0) {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/contacts`,
					state: {
						contactIds: selectedContactIds,
					},
				});
			} else {
				fullscreenModal.history.push({
					pathname: `/texting-campaign/filter`,
					state: {
						initialContactIdsToOmit: excludedContactIds,
						initialFilterCriteriaList: filter.filter.criteria,
						initialOwnershipFilter: filter.ownershipFilter,
					},
				});
			}
		};

		const onHwcTemplateClicked = (template: Api.IHandwrittenCardTemplate) => {
			if (routeAfterHwcTemplateSelection === 'contacts') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-selection-postcard/${template.id}`,
					state: {
						contactIds: selectedContactIds,
					},
				});
			} else if (routeAfterHwcTemplateSelection === 'filter') {
				fullscreenModal.history.push({
					pathname: `/bulk-contact-filter-postcard/${template.id}`,
					state: {
						contactIdsToOmit: excludedContactIds,
						tagFilterCriterias: filter.filter.criteria,
						ownershipFilter: filter.ownershipFilter,
					},
				});
			}
			setIsHwcTemplateChooserModalOpen(false);
			setRouteAfterHwcTemplateSelection(null);
		};

		// Modal handlers
		const restoreModal = useModal(
			false,
			(result: IConfirmationDialogOption) => {
				if (result.isCta) {
					contactUnarchiveMutation.mutate({
						excludeContactIds: excludedContactIds,
						filter: isSelectingAll ? filter.filter : null,
						ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
						includeContactIds: selectedContactIds,
					});
				}
			},
			[contactUnarchiveMutation, selectedContactIds, excludedContactIds, isSelectingAll, filter]
		);

		const keepInTouchModal = useModal(
			false,
			(frequency: number) => {
				onKeepInTouch(frequency);
			},
			[onKeepInTouch]
		);

		const addTagModal = useModal<string>(
			false,
			_selectedTag => {
				invalidateAllContactFilterQueries();
			},
			[invalidateAllContactFilterQueries]
		);

		const openMergeModal = () => {
			setIsMergeModalOpen(true);
		};

		const closeMergeModal = () => {
			clearSelectionState();
			setIsMergeModalOpen(false);
		};

		const openContactOwnerModal = () => {
			setIsContactOwnerModalOpen(true);
			setIsMoreMenuOpen(false);
		};

		const openContactConnectionModal = () => {
			setIsContactConnectionModalOpen(true);
			setIsMoreMenuOpen(false);
		};

		const onExportContactsButtonClicked = () => {
			if (exportContactsMutation.isIdle) {
				setIsMoreMenuOpen(false);

				// Create export request
				const exportRequest = {
					excludeContactIds: excludedContactIds,
					filter: isSelectingAll ? filter.filter : null,
					ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
					includeContactIds: selectedContactIds,
				};

				exportContactsMutation.mutate({ exportRequest });
			}
		};

		const onExportContactsClose = () => {
			exportContactsMutation.reset();
		};

		const onExportContactsPolicyReportButtonClicked = () => {
			if (exportContactsPolicyReportMutation.isIdle) {
				setIsMoreMenuOpen(false);

				// Create export request
				const exportRequest = {
					excludeContactIds: excludedContactIds,
					filter: isSelectingAll ? filter.filter : null,
					ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
					includeContactIds: selectedContactIds,
				};

				exportContactsPolicyReportMutation.mutate({ exportRequest });
			}
		};

		const onExportContactsPolicyReportModalClose = () => {
			exportContactsPolicyReportMutation.reset();
		};

		const deleteModal = useModal(
			false,
			async (shouldDelete: boolean) => {
				if (!shouldDelete) {
					return;
				}

				if (isSelectingAll) {
					// Use the deletion with filter mutation
					await contactDeleteManyMutation.mutateAsync({
						excludeContactIds: excludedContactIds,
						filter: filter.filter,
						ownershipFilter: filter.ownershipFilter,
						includeContactIds: [],
					});
					invalidateAllContactFilterQueries();
				} else {
					// Use the standard deletion mutation for selected contacts
					await contactDeleteMutation.mutateAsync({ ids: selectedContactIds });
					invalidateAllContactFilterQueries();
				}
			},
			[isSelectingAll, contactDeleteManyMutation, contactDeleteMutation, excludedContactIds, filter, selectedContactIds]
		);

		const onDeleteClicked = () => {
			deleteModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const confirmationDialogModal = useModal(
			false,
			(result: IConfirmationDialogOption) => {
				if (result) {
					onVisibilityChanged(visibilityChangeType);
				}
				setVisibilityChangeType(null);
			},
			[visibilityChangeType]
		);

		const openConfirmationDialogModal = (visibility: 'admin' | 'all') => () => {
			setVisibilityChangeType(visibility);
			confirmationDialogModal.setIsOpen(true)();
			setIsMoreMenuOpen(false);
		};

		const onVisibilityChanged = (visibility: 'admin' | 'all') => {
			const request = {
				excludeContactIds: excludedContactIds,
				filter: isSelectingAll ? filter.filter : null,
				ownershipFilter: isSelectingAll ? filter.ownershipFilter : null,
				includeContactIds: selectedContactIds,
				visibility,
			};

			contactSetVisibilityMutation.mutate(request);
		};

		// Reference to measure container width
		const containerRef = React.useRef<HTMLDivElement>(null);

		// Track container width
		const [containerWidth, setContainerWidth] = React.useState(0);
		// Track whether the keep in touch dropdown is open
		const [isKeepInTouchOpen, setIsKeepInTouchOpen] = React.useState(false);

		// Use ResizeObserver to detect container width changes
		React.useEffect(() => {
			if (!containerRef.current) return;

			const resizeObserver = new ResizeObserver(entries =>
				debounce(() => {
					if (entries[0]) {
						setContainerWidth(entries[0].contentRect.width);
					}
				}, 1)()
			);

			resizeObserver.observe(containerRef.current);
			return () => {
				resizeObserver.disconnect();
			};
		}, []);

		// Get action buttons
		const allButtons = useActionButtons({
			isActive,
			canKeepInTouch: !!canKeepInTouch,
			canMerge,
			canFocus,
			canRestore,
			isArchived,
			canShowAutomations,
			canShowCards,
			canSendTextingCampaign,
			onKeepInTouch,
			onSendMessage,
			onSendTextClicked,
			onSendCard,
			onFocusContacts,
			openConfirmationDialogModal,
			openContactOwnerModal,
			openContactConnectionModal,
			openMergeModal,
			onDeleteClicked,
			addTagModal,
			restoreModal,
			keepInTouchModal,
			onExportContactsButtonClicked,
			onExportContactsPolicyReportButtonClicked,
			setIsAutomationOpen,
			selectionState,
		});

		// Determine which buttons to show and which to put in "More" menu
		const { visibleButtons: buttonVisibleButtons, moreMenuButtons: buttonMoreMenuButtons } = useButtonConfiguration(
			allButtons,
			containerWidth
		);

		// Find the keep in touch button to use in the popover
		const keepInTouchButton = React.useMemo(() => {
			return allButtons.find(btn => btn.key === 'keepInTouch');
		}, [allButtons]);

		return (
			<div ref={containerRef} className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
				{userSession.userRole === 'superAdmin' && isArchived() ? (
					<>
						<ActionBarButton
							icon={<ArchiveIcon />}
							onClick={restoreModal.setIsOpen(true)}
							disabled={!canRestore}
							styleClass={styleSheet.keepInTouch}
							actionButtonType='standard'
						>
							Restore selected contacts
						</ActionBarButton>
						<ConfirmationDialog
							modalProps={{
								isOpen: restoreModal.isOpen,
								onRequestClose: restoreModal.onRequestClose,
							}}
							options={RestoreConfirmationOptions}
							title='Restore selected contacts'
						>
							<div className={css(styleSheet.restoreContactText)}>Would you like to restore the selected contacts?</div>
						</ConfirmationDialog>
					</>
				) : (
					<>
						<div className={css(styleSheet.actionButtonsContainer)}>
							{/* Other visible buttons */}
							<ButtonRenderer
								allButtons={allButtons.filter(btn => btn.key !== 'keepInTouch')}
								visibleButtons={buttonVisibleButtons.filter(key => key !== 'keepInTouch')}
							/>
							{/* Keep in Touch Button with Popover */}
							{keepInTouchButton && buttonVisibleButtons.includes('keepInTouch') && (
								<Popover open={isKeepInTouchOpen} onOpenChange={setIsKeepInTouchOpen}>
									<PopoverTrigger asChild>
										<button
											onClick={() => setIsKeepInTouchOpen(!isKeepInTouchOpen)}
											className={css(
												styleSheet.actionButton,
												keepInTouchButton.disabled ? styleSheet.actionButtonDisabled : styleSheet.actionButtonEnabled,
												keepInTouchButton.styleClass
											)}
											disabled={keepInTouchButton.disabled}
										>
											{keepInTouchButton.icon}
											<span className={css(styleSheet.actionButtonText)}>{keepInTouchButton.children}</span>
										</button>
									</PopoverTrigger>
									<PopoverContent>
										<div className={css(styleSheet.keepInTouchMenu)}>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(1);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every month</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(2);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 2 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(3);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 3 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(6);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every 6 months</span>
											</button>
											<button
												className={css(styleSheet.keepInTouchMenuItem)}
												onClick={() => {
													onKeepInTouch(12);
													setIsKeepInTouchOpen(false);
												}}
											>
												<span>Every year</span>
											</button>
										</div>
									</PopoverContent>
								</Popover>
							)}

							{buttonMoreMenuButtons.length > 0 && (
								<Popover open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
									<PopoverTrigger asChild>
										<button
											onClick={() => {
												setIsMoreMenuOpen(!isMoreMenuOpen);
											}}
										>
											<MoreIcon />
										</button>
									</PopoverTrigger>
									<PopoverContent>
										<div className={css(styleSheet.moreMenuContainer)}>
											{buttonMoreMenuButtons.map(btn => {
												return (
													<div key={btn.key} className={css(styleSheet.moreMenuButton)}>
														<ActionBarButton
															key={btn.key}
															icon={btn.icon}
															onClick={btn.onClick}
															disabled={btn.disabled}
															styleClass={btn.styleClass}
															isFullWidth
															actionButtonType={btn.actionButtonType}
														>
															{btn.children}
														</ActionBarButton>
													</div>
												);
											})}
										</div>
									</PopoverContent>
								</Popover>
							)}
						</div>
						<ContactsAddTagModal
							request={{
								excludeContactIds: excludedContactIds,
								includeContactIds: selectedContactIds,
								filter: isSelectingAll ? filter.filter : undefined,
								ownershipFilter: isSelectingAll ? filter.ownershipFilter : undefined,
							}}
							modalProps={{
								isOpen: addTagModal.isOpen,
								onRequestClose: addTagModal.onRequestClose,
							}}
						/>
						<AddContactConnection
							selectedContactIds={selectedContactIds}
							excludedContactIds={excludedContactIds}
							filterCriteria={filter.filter}
							ownershipFilter={filter.ownershipFilter}
							includeFilterInRequest={isSelectingAll}
							isOpen={isContactConnectionModalOpen}
							onSave={() => setIsContactConnectionModalOpen(false)}
							onCancel={() => setIsContactConnectionModalOpen(false)}
						/>
						<AssignContactOwner
							selectedContactIds={selectedContactIds}
							excludedContactIds={excludedContactIds}
							filterCriteria={filter.filter}
							ownershipFilter={filter.ownershipFilter}
							includeFilterInRequest={isSelectingAll}
							isOpen={isContactOwnerModalOpen}
							onSuccess={() => setIsContactOwnerModalOpen(false)}
							onCancel={() => setIsContactOwnerModalOpen(false)}
						/>
						{isAutomationOpen ? (
							<AutomationSelectorModal
								contactSelection={{
									includeContactIds: selectedContactIds,
									excludeContactIds: excludedContactIds,
								}}
								filterCriteria={isSelectingAll ? filter.filter : null}
								ownershipFilter={isSelectingAll ? filter.ownershipFilter : null}
								onDeselectAll={() => setSelectedContactIds([])}
								closeModal={onAutomationsSuccess}
								startAutomationForSelectedContacts={true}
							/>
						) : null}
						<ConfirmationDialog
							className={css(styleSheet.visibilityModalContainer)}
							icon={<WarningIcon />}
							modalProps={{
								isOpen: confirmationDialogModal.isOpen,
								onRequestClose: confirmationDialogModal.onRequestClose,
							}}
							options={ConfirmVisibilityChange}
						>
							<div>
								<div className={css(styleSheet.visibilityModalTitle)}>Change visibility</div>
								<div className={css(styleSheet.visibilityModalTextContainer)}>
									Are you sure you want to change the visibility of selected contacts so
									<span className={css(styleSheet.visibilityModalTextBold)}>
										{visibilityChangeType === 'admin'
											? ' only you can see them'
											: ' all Levitate users on your account can see them'}
										?
									</span>
								</div>
							</div>
						</ConfirmationDialog>
						{isMergeModalOpen && <MergeContactsWizard selectedContacts={selectedContacts} onFinish={closeMergeModal} />}
						<ExportConfirmationModalV2
							mutationResult={exportContactsMutation}
							modalProps={{
								isOpen: !exportContactsMutation.isIdle,
								onRequestClose: onExportContactsClose,
							}}
							subTitle={
								<>
									<p className={css(styleSheet.noMargin)}>
										The fields we&apos;ll export include: Name, Company, Job Title,
									</p>
									<p className={css(styleSheet.noMargin)}>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
								</>
							}
						/>
						<ExportConfirmationModalV2
							mutationResult={exportContactsPolicyReportMutation}
							modalProps={{
								isOpen: !exportContactsPolicyReportMutation.isIdle,
								onRequestClose: onExportContactsPolicyReportModalClose,
							}}
							subTitle={
								<>
									<p className={css(styleSheet.noMargin)}>
										The fields we&apos;ll export include: Name, First Name, Email,
									</p>
									<p className={css(styleSheet.noMargin)}>Owner, Line of Business, Carrier, and Renewal Date.</p>
								</>
							}
						/>
						<DeleteConfirmation
							bodyText='Are you sure you want to delete these contacts?'
							isOpen={deleteModal.isOpen}
							onFinish={deleteModal.onRequestClose}
						/>
						{isHwcTemplateChooserModalOpen ? (
							<HandwrittenCardTemplateSearchModal
								isOpen={true}
								onClose={() => setIsHwcTemplateChooserModalOpen(false)}
								onTemplateClicked={onHwcTemplateClicked}
							/>
						) : null}
					</>
				)}
			</div>
		);
	}
);
